import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useObepService } from "../apis/obep/useObepService";
import {
  TCreateObepRecordRequest,
  TCreateObepRecordResponse,
  TObepListFilters,
  TObepRecord,
  TUpdateObepRecordRequest,
} from "../apis/obep/obep.interfaces";
import { useInstituteContext } from "../context/institute.context";

export const useObepListHook = (filters?: TObepListFilters) => {
  const { getObepRecords } = useObepService();
  const { selectedInstitute } = useInstituteContext();

  const instituteId = selectedInstitute?.id;
  const mergedFilters: TObepListFilters = {
    page: filters?.page ?? 1,
    limit: filters?.limit ?? 20,
    ...(instituteId ? { instituteId } : {}),
  };

  return useQuery<TObepRecord[]>({
    queryKey: ["obep-records", instituteId, mergedFilters],
    queryFn: () => getObepRecords(mergedFilters),
    enabled: !!instituteId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useObepByIdHook = (obepId?: string) => {
  const { getObepRecordById } = useObepService();

  return useQuery<TObepRecord>({
    queryKey: ["obep-record", obepId],
    queryFn: () => getObepRecordById(obepId!),
    enabled: !!obepId,
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateObepHook = () => {
  const { createObepRecord } = useObepService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return useMutation<
    TCreateObepRecordResponse,
    Error,
    TCreateObepRecordRequest
  >({
    mutationFn: (request) => createObepRecord(request),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["obep-records", selectedInstitute?.id],
      });
    },
  });
};

export const useUpdateObepHook = () => {
  const { updateObepRecord } = useObepService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  return useMutation<
    TObepRecord,
    Error,
    { obepId: string; request: TUpdateObepRecordRequest }
  >({
    mutationFn: ({ obepId, request }) => updateObepRecord(obepId, request),
    onSuccess: (data, variables) => {
      queryClient.setQueryData<TObepRecord>(
        ["obep-record", variables.obepId],
        (prev) => ({
          ...(prev ?? data),
          ...data,
          ...(variables.request.description !== undefined
            ? { description: variables.request.description }
            : {}),
          ...(variables.request.objectives !== undefined
            ? { objectives: variables.request.objectives }
            : {}),
        }),
      );
      queryClient.invalidateQueries({
        queryKey: ["obep-records", selectedInstitute?.id],
      });
    },
  });
};
